package com.eladfinish.bible.contest.screens.main.data

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.graphics.vector.PathParser
import androidx.compose.ui.unit.dp

/**
 * Custom "open questions" icon: a speech bubble with a "?" (the question) above two
 * free-text answer lines (the open, write-your-own response) — as opposed to the
 * fixed options of a multiple-choice question.
 *
 * The "?" outline is extracted from a bold sans font. The icon is tinted at render
 * time, so the colors here are irrelevant.
 */
val OpenQuestionsIcon: ImageVector by lazy {
    fun nodes(pathData: String) = PathParser().parsePathString(pathData).toNodes()
    val tint = SolidColor(Color.Black)
    ImageVector.Builder(
        name = "OpenQuestions",
        defaultWidth = 24.dp,
        defaultHeight = 24.dp,
        viewportWidth = 24f,
        viewportHeight = 24f,
    ).apply {
        addPath(
            pathData = nodes(BUBBLE_PATH),
            stroke = tint,
            strokeLineWidth = 1.5f,
            strokeLineJoin = StrokeJoin.Round,
        )
        addPath(pathData = nodes(QUESTION_MARK_PATH), fill = tint)
        addPath(pathData = nodes(LINE_1_PATH), stroke = tint, strokeLineWidth = 1.6f, strokeLineCap = StrokeCap.Round)
        addPath(pathData = nodes(LINE_2_PATH), stroke = tint, strokeLineWidth = 1.6f, strokeLineCap = StrokeCap.Round)
    }.build()
}

private const val BUBBLE_PATH =
    "M6.0 2.6 H18.0 A3.2 3.2 0 0 1 21.2 5.8 V11.0 A3.2 3.2 0 0 1 18.0 14.2 " +
        "H10.5 L7.2 17.4 L7.2 14.2 H6.0 A3.2 3.2 0 0 1 2.8 11.0 V5.8 A3.2 3.2 0 0 1 6.0 2.6 Z"

private const val QUESTION_MARK_PATH =
    "M12.452471482889734 8.078326996197719H11.482889733840304Q11.479087452471482 7.869201520912547 11.479087452471482 7.823574144486692Q11.479087452471482 7.352091254752851 11.634980988593156 7.047908745247148Q11.790874524714829 6.743726235741445 12.258555133079849 6.363498098859315Q12.726235741444867 5.983269961977186 12.817490494296578 5.865399239543726Q12.958174904942965 5.679087452471483 12.958174904942965 5.454752851711027Q12.958174904942965 5.142965779467681 12.70912547528517 4.920532319391635Q12.460076045627376 4.698098859315589 12.038022813688212 4.698098859315589Q11.631178707224334 4.698098859315589 11.357414448669202 4.930038022813688Q11.083650190114069 5.161977186311788 10.980988593155894 5.637262357414449L10.0 5.515589353612167Q10.041825095057034 4.834980988593156 10.579847908745247 4.359695817490494Q11.11787072243346 3.884410646387833 11.992395437262356 3.884410646387833Q12.91254752851711 3.884410646387833 13.456273764258555 4.365399239543726Q14.0 4.8463878326996195 14.0 5.485171102661597Q14.0 5.8387832699619775 13.800380228136882 6.154372623574145Q13.600760456273765 6.469961977186312 12.9467680608365 7.013688212927757Q12.608365019011407 7.295057034220532 12.526615969581748 7.46615969581749Q12.44486692015209 7.637262357414449 12.452471482889734 8.078326996197719ZM11.482889733840304 9.515589353612167V8.447148288973384H12.551330798479087V9.515589353612167Z"

private const val LINE_1_PATH = "M4.2 19.4 H19.8"

private const val LINE_2_PATH = "M4.2 22.0 H13.0"
